import { z } from 'zod'
import { codePointLength, trimForValidate } from '@/utils/stringLength'

/**
 * 企业/机构/项目名称：工商登记名称常见 2–100 字符；
 * 单行文本系统默认上限 255，表单取 100 防误填。
 */
export const ENTERPRISE_NAME_LIMITS = {
  min: 2,
  max: 100,
} as const

const EMOJI_PATTERN = /\p{Extended_Pictographic}/u

/** 企业名称常用字符：中英文、数字、括号、书名号、& 等 */
const ENTERPRISE_NAME_PATTERN = /^[\p{L}\p{N}\s()（）《》·\-—&.,''""''""\u0026]+$/u

function assertEnterpriseName(value: string, label: string, ctx: z.RefinementCtx) {
  const len = codePointLength(value)
  if (len < ENTERPRISE_NAME_LIMITS.min) {
    ctx.addIssue({
      code: 'custom',
      message: `${label}至少 ${ENTERPRISE_NAME_LIMITS.min} 个字符`,
    })
  }
  if (len > ENTERPRISE_NAME_LIMITS.max) {
    ctx.addIssue({
      code: 'custom',
      message: `${label}不能超过 ${ENTERPRISE_NAME_LIMITS.max} 个字符`,
    })
  }
  if (EMOJI_PATTERN.test(value)) {
    ctx.addIssue({ code: 'custom', message: `${label}不能包含表情符号` })
  }
  if (!ENTERPRISE_NAME_PATTERN.test(value)) {
    ctx.addIssue({ code: 'custom', message: `${label}含非法字符` })
  }
}

/** 企业/机构/项目名称（必填） */
export const enterpriseNameSchema = z
  .string()
  .superRefine((raw, ctx) => {
    const v = trimForValidate(raw)
    if (!v) {
      ctx.addIssue({ code: 'custom', message: '请输入名称' })
      return
    }
    assertEnterpriseName(v, '名称', ctx)
  })
  .transform(trimForValidate)

/** 选填企业名称 */
export const optionalEnterpriseNameSchema = z
  .string()
  .superRefine((raw, ctx) => {
    const v = trimForValidate(raw)
    if (!v) return
    assertEnterpriseName(v, '名称', ctx)
  })
  .transform(trimForValidate)

export function isValidEnterpriseName(value: string): boolean {
  return enterpriseNameSchema.safeParse(value).success
}
